import Css from "./style.module.scss";

import { getSelectedBusinessData } from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { useSelector } from "react-redux";
import Badge from "nlib/ui/Badge";
import ConfidencePercents from "nlib/common/ConfidencePercents";
import DataStatuses from "const/DataStatuses";
import DocumentRecogniseStatuses from "const/DocumentRecogniseStatuses";
import DocumentsStatuses from "nlib/pages/DocumentsPage/DocumentsStatuses";
import IntegrationServices from "const/IntegrationServices";
import React, { useMemo } from "react";
import classNames from "classnames";

const Content = ({ errorState, duplicatedDocument, toReviewStatus, recogniseData }) => {
  const { uiTexts } = useSelector(getTextsData);

  if (errorState) {
    return (
      <Badge negative className={Css.badge} value={uiTexts.error} />
    );
  }
  if (duplicatedDocument) {
    return (
      <Badge warning className={Css.badge} value={uiTexts.duplicate} />
    );
  }
  if (toReviewStatus && !recogniseData.confidence) {
    return (
      <Badge primary className={Css.badge} value={uiTexts.edited} />
    );
  }
  if (recogniseData.confidence) {
    return (
      <ConfidencePercents
        className={classNames(Css.badge, Css.confidence)}
        value={recogniseData.averageConfidence}
        component={Badge} />
    );
  }

  return null;
};

const StatusCell = ({ documentData }) => {
  const { status, paid, recogniseData = {}, duplicatedDocumentsIds } = documentData;

  const { uiTexts } = useSelector(getTextsData);

  const selectedBusinessData = useSelector(getSelectedBusinessData);

  const {
    labelLangId,
    iconComponent: StatusIcon,
    errorIconComponent: ErrorStatusIcon
  } = useMemo(() => {
    return DocumentsStatuses.getStatusData(status);
  }, [status]);

  const { extraData: { integrationService } = {} } = selectedBusinessData;

  const { logo: serviceLogo } = IntegrationServices.getByValue(integrationService) || {};

  const toReviewStatus = status === DataStatuses.TO_REVIEW;

  const errorState = status === DataStatuses.TO_EXTRACT && recogniseData.status === DocumentRecogniseStatuses.ERROR;

  const innerStatus = errorState ? recogniseData.status : status;

  const Icon = useMemo(() => {
    if (errorState) return ErrorStatusIcon;
    if (status === DataStatuses.EXPORTED) return () => <img src={serviceLogo} className={Css.serviceLogo} />;

    return StatusIcon;
  }, [errorState, status, serviceLogo, StatusIcon, ErrorStatusIcon]);

  const statusTitle = useMemo(() => {
    if (errorState) return uiTexts.error;
    if (status === DataStatuses.EXPORTED) {
      if (paid) return uiTexts.paid;

      return uiTexts.unpaid;
    }

    return uiTexts[labelLangId];
  }, [errorState, status, paid, labelLangId, uiTexts]);

  const duplicatedDocument = Array.isArray(duplicatedDocumentsIds) && duplicatedDocumentsIds.length;

  const withBadge = errorState || duplicatedDocument || (toReviewStatus && !recogniseData.confidence) || recogniseData.confidence;

  return (
    <div
      title={statusTitle}
      data-status={innerStatus}
      className={classNames(
        Css.statusCell,
        Css[innerStatus],
        withBadge && Css.withBadge
      )}>
      <Icon />
      <Content
        errorState={errorState}
        duplicatedDocument={duplicatedDocument}
        toReviewStatus={toReviewStatus}
        recogniseData={recogniseData} />
    </div>
  );
};

export default React.memo(StatusCell);
